import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  IconButton,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../components/DashboardLayout';
import { getWhitePaperRequests, deleteWhitePaperRequest } from '../services/WhitePaperRequestService';
import * as XLSX from 'xlsx';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function WhitePaperRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedRequests,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(requests, 10);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const data = await getWhitePaperRequests();
      if (Array.isArray(data)) {
        setRequests(data);
      } else {
        setRequests([]);
      }
    } catch (err) {
      console.error('Error fetching white-paper requests:', err);
      setError('Failed to load white-paper requests.');
    }
  };

  const handleDelete = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this request?')) {
      try {
        await deleteWhitePaperRequest(requestId);
        fetchRequests(); // Refresh list after deletion
      } catch (err) {
        console.error('Error deleting request:', err);
        setError('Failed to delete request.');
      }
    }
  };

  /**
   * Export to Excel, including:
   * Name, CompanyName, Position, WorkEmail, PhoneNumber,
   * WhitePaperId, WhitePaperTitle
   */
  const handleExportToExcel = () => {
    // Map only the columns we want in the export
    const minimalData = requests.map((req) => ({
      Name: req.name,
      CompanyName: req.companyName,
      Position: req.position,
      WorkEmail: req.workEmail,
      PhoneNumber: req.phoneNumber,
      WhitePaperId: req.whitePaperId,
      WhitePaperTitle: req.whitePaperTitle
    }));

    const worksheet = XLSX.utils.json_to_sheet(minimalData, {
      header: [
        'Name',
        'CompanyName',
        'Position',
        'WorkEmail',
        'PhoneNumber',
        'WhitePaperId',
        'WhitePaperTitle'
      ]
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Requests');

    // Optional column widths
    worksheet['!cols'] = [
      { wch: 20 }, // Name
      { wch: 20 }, // CompanyName
      { wch: 15 }, // Position
      { wch: 25 }, // WorkEmail
      { wch: 15 }, // PhoneNumber
      { wch: 15 }, // WhitePaperId
      { wch: 30 }  // WhitePaperTitle
    ];

    XLSX.writeFile(workbook, 'WhitePaperRequests.xlsx');
  };

  return (
    <DashboardLayout>
      <Container>
        <Box mt={4}>
          <AdminPageHeader
            eyebrow="Operations"
            title="White Paper Requests"
            description="Track gated download requests and export lead details when needed."
            action={<Button variant="outlined" onClick={handleExportToExcel}>Export to Excel</Button>}
          />
          {error && <Typography color="error" gutterBottom sx={{ mt: 2 }}>{error}</Typography>}

          {isMobile ? (
            // Mobile card layout
            <Grid container spacing={2}>
              {paginatedRequests.map((req) => (
                <Grid item xs={12} key={req.requestId}>
                  <AdminSection sx={{ p: 0 }}>
                  <Paper variant="outlined" sx={{ border: 0, boxShadow: 'none' }}>
                    <Box p={2}>
                      <Typography variant="h6">Name: {req.name}</Typography>
                      <Typography>Company: {req.companyName}</Typography>
                      <Typography>Position: {req.position}</Typography>
                      <Typography>Work Email: {req.workEmail}</Typography>
                      <Typography>Phone: {req.phoneNumber}</Typography>
                      <Typography>WhitePaper ID: {req.whitePaperId}</Typography>
                      <Typography>Title: {req.whitePaperTitle}</Typography>
                      <Box display="flex" justifyContent="flex-end" mt={2}>
                        <IconButton color="error" onClick={() => handleDelete(req.requestId)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                  </AdminSection>
                </Grid>
              ))}
              {requests.length === 0 && (
                <Grid item xs={12}>
                  <AdminSection>
                    <AdminEmptyState title="No white paper requests yet" description="Lead requests will appear here once downloads start coming in." />
                  </AdminSection>
                </Grid>
              )}
            </Grid>
          ) : (
            // Desktop table layout
            <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Work Email</TableCell>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>WhitePaper ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRequests.map((req) => (
                    <TableRow key={req.requestId}>
                      <TableCell>{req.name}</TableCell>
                      <TableCell>{req.companyName}</TableCell>
                      <TableCell>{req.position}</TableCell>
                      <TableCell>{req.workEmail}</TableCell>
                      <TableCell>{req.phoneNumber}</TableCell>
                      <TableCell>{req.whitePaperId}</TableCell>
                      <TableCell>{req.whitePaperTitle}</TableCell>
                      <TableCell align="right">
                        <IconButton color="error" onClick={() => handleDelete(req.requestId)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {requests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <AdminEmptyState title="No white paper requests yet" description="Lead requests will appear here once downloads start coming in." />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <AdminPagination
                count={count}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </AdminSection>
          )}
          {isMobile ? (
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          ) : null}
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default WhitePaperRequestsPage;
